import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import {
  getMisPerfilesFacturacion,
  crearPerfilFacturacion,
} from '../lib/perfilesFacturacionApi'
import { validarRutUY, limpiarRut, formatearRut } from '../lib/rutUY'

/**
 * Bloque "¿A nombre de quién facturamos?" para usar en checkouts.
 *
 * Comportamiento:
 *  - 0 perfiles: 2 botones grandes (CF crea inline / Empresa abre mini-form)
 *  - 1 perfil: resumen + link a /cliente/facturacion para agregar otro
 *  - 2+ perfiles: dropdown obligatorio (default preseleccionado)
 *
 * Props:
 *  - value: perfil_facturacion_id seleccionado (string|null)
 *  - onChange(id): callback al elegir/crear un perfil
 *  - className: clase opcional para el wrapper
 */
export default function BloqueFacturacion({ value, onChange, className = '' }) {
  const { profile } = useAuth()
  const [perfiles, setPerfiles] = useState([])
  const [cargando, setCargando] = useState(true)
  const [mostrarFormEmpresa, setMostrarFormEmpresa] = useState(false)
  const [formEmp, setFormEmp] = useState({
    alias: '', razonSocial: '', rut: '', direccionFiscal: '', emailFiscal: '',
  })
  const [guardando, setGuardando] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setCargando(true)
    const list = await getMisPerfilesFacturacion()
    setPerfiles(list)
    setCargando(false)
    // Auto-select default si el padre no eligió todavía
    if (!value) {
      const def = list.find(p => p.es_default) || list[0]
      if (def) onChange?.(def.id)
    }
  }

  /** Caso 1: crear CF automático con el nombre del profile. */
  async function crearCFRapido() {
    setErr(null); setGuardando(true)
    try {
      const nombre = profile?.nombre?.trim() || 'Personal'
      const p = await crearPerfilFacturacion({
        tipo: 'consumidor_final',
        alias: nombre,
        esDefault: true,
      })
      await cargar()
      onChange?.(p.id)
    } catch (e) {
      setErr(e?.message || 'No se pudo crear el perfil')
    }
    setGuardando(false)
  }

  /** Caso 2: crear empresa desde el form mini. */
  async function crearEmpresa(e) {
    e?.preventDefault()
    setErr(null)
    if (!formEmp.alias.trim()) { setErr('Falta alias (ej: "Mi SAS")'); return }
    if (!formEmp.razonSocial.trim()) { setErr('Falta razón social'); return }
    if (!validarRutUY(formEmp.rut)) { setErr('RUT inválido (12 dígitos)'); return }
    setGuardando(true)
    try {
      const p = await crearPerfilFacturacion({
        tipo: 'empresa',
        alias: formEmp.alias,
        razonSocial: formEmp.razonSocial,
        rut: limpiarRut(formEmp.rut),
        direccionFiscal: formEmp.direccionFiscal,
        emailFiscal: formEmp.emailFiscal,
        esDefault: perfiles.length === 0,
      })
      await cargar()
      onChange?.(p.id)
      setMostrarFormEmpresa(false)
      setFormEmp({ alias: '', razonSocial: '', rut: '', direccionFiscal: '', emailFiscal: '' })
    } catch (e2) {
      setErr(e2?.message || 'No se pudo crear el perfil')
    }
    setGuardando(false)
  }

  if (cargando) {
    return <div className={`bf ${className}`}><div className="bf-loading">Cargando…</div><style>{css}</style></div>
  }

  const perfilElegido = perfiles.find(p => p.id === value)

  return (
    <div className={`bf ${className}`}>
      <div className="bf-title">
        <span className="bf-icon">🧾</span>
        <span>¿A nombre de quién facturamos?</span>
      </div>

      {/* ───── Caso A: 0 perfiles ───── */}
      {perfiles.length === 0 && !mostrarFormEmpresa && (
        <>
          <p className="bf-sub">Elegí cómo te facturamos. Después lo podés cambiar en Mi cuenta.</p>
          <div className="bf-choice">
            <button type="button" className="bf-choice-card" onClick={crearCFRapido} disabled={guardando}>
              <div className="bf-choice-icon">👤</div>
              <div className="bf-choice-h">Consumidor final</div>
              <div className="bf-choice-p">e-Ticket a tu nombre. La opción más común.</div>
            </button>
            <button type="button" className="bf-choice-card" onClick={() => setMostrarFormEmpresa(true)} disabled={guardando}>
              <div className="bf-choice-icon">🏢</div>
              <div className="bf-choice-h">Empresa con RUT</div>
              <div className="bf-choice-p">e-Factura con razón social y RUT.</div>
            </button>
          </div>
        </>
      )}

      {/* ───── Form mini empresa ───── */}
      {perfiles.length === 0 && mostrarFormEmpresa && (
        <form className="bf-form" onSubmit={crearEmpresa}>
          <label className="bf-field">
            <span>Alias <em>*</em></span>
            <input type="text" value={formEmp.alias}
                   onChange={e => setFormEmp(f => ({ ...f, alias: e.target.value }))}
                   placeholder="Mi SAS" maxLength={60} required />
          </label>
          <label className="bf-field">
            <span>Razón social <em>*</em></span>
            <input type="text" value={formEmp.razonSocial}
                   onChange={e => setFormEmp(f => ({ ...f, razonSocial: e.target.value }))}
                   placeholder="Mi Empresa SAS" maxLength={120} required />
          </label>
          <label className="bf-field">
            <span>RUT <em>*</em></span>
            <input type="text" value={formEmp.rut}
                   onChange={e => setFormEmp(f => ({ ...f, rut: e.target.value }))}
                   placeholder="21.234.567.890-3" inputMode="numeric" maxLength={20} required />
          </label>
          <label className="bf-field">
            <span>Domicilio fiscal</span>
            <input type="text" value={formEmp.direccionFiscal}
                   onChange={e => setFormEmp(f => ({ ...f, direccionFiscal: e.target.value }))}
                   placeholder="Av. Italia 1234" />
          </label>
          <label className="bf-field">
            <span>Email facturación</span>
            <input type="email" value={formEmp.emailFiscal}
                   onChange={e => setFormEmp(f => ({ ...f, emailFiscal: e.target.value }))}
                   placeholder="facturacion@miempresa.uy" />
          </label>
          {err && <div className="bf-err">{err}</div>}
          <div className="bf-form-actions">
            <button type="button" className="bf-btn-secondary" onClick={() => { setMostrarFormEmpresa(false); setErr(null) }}>
              ← Volver
            </button>
            <button type="submit" className="bf-btn-primary" disabled={guardando}>
              {guardando ? 'Guardando…' : 'Guardar empresa'}
            </button>
          </div>
        </form>
      )}

      {/* ───── Caso B: 1 perfil ───── */}
      {perfiles.length === 1 && perfilElegido && (
        <div className="bf-uno">
          <div className="bf-uno-l">
            <div className={`bf-tipo-pill ${perfilElegido.tipo}`}>
              {perfilElegido.tipo === 'empresa' ? '🏢 Empresa' : '👤 Consumidor final'}
            </div>
            <div className="bf-uno-info">
              <div className="bf-uno-alias">{perfilElegido.alias}</div>
              {perfilElegido.tipo === 'empresa' && (
                <div className="bf-uno-rut">{perfilElegido.razon_social} · RUT {formatearRut(perfilElegido.rut)}</div>
              )}
            </div>
          </div>
          <Link to="/cliente/facturacion" className="bf-uno-link" target="_blank" rel="noopener">
            Agregar otro →
          </Link>
        </div>
      )}

      {/* ───── Caso C: 2+ perfiles ───── */}
      {perfiles.length > 1 && (
        <div className="bf-multi">
          <select
            value={value || ''}
            onChange={e => onChange?.(e.target.value)}
            className="bf-select"
          >
            {!value && <option value="">— elegir —</option>}
            {perfiles.map(p => (
              <option key={p.id} value={p.id}>
                {p.tipo === 'empresa' ? '🏢 ' : '👤 '}
                {p.alias}
                {p.tipo === 'empresa' && ` · ${p.razon_social}`}
              </option>
            ))}
          </select>
          <Link to="/cliente/facturacion" className="bf-multi-link" target="_blank" rel="noopener">
            + Agregar
          </Link>
        </div>
      )}

      <style>{css}</style>
    </div>
  )
}

const css = `
.bf {
  background: #fff; border: 1.5px solid var(--cl-line, #e2e8f0);
  border-radius: 14px; padding: 14px 16px;
  font-family: -apple-system, BlinkMacSystemFont, "Inter", system-ui, sans-serif;
}
.bf-loading { padding: 12px; text-align: center; color: var(--cl-muted, #64748b); font-size: 13px }
.bf-title {
  display: flex; align-items: center; gap: 8px;
  font-size: 14px; font-weight: 800; color: var(--cl-navy, #1A3461); margin-bottom: 4px;
}
.bf-icon { font-size: 18px }
.bf-sub { font-size: 12.5px; color: var(--cl-muted, #64748b); margin: 0 0 12px; line-height: 1.5 }

/* Caso A: 0 perfiles → 2 cards */
.bf-choice { display: grid; grid-template-columns: 1fr; gap: 10px }
@media (min-width: 520px) { .bf-choice { grid-template-columns: 1fr 1fr } }
.bf-choice-card {
  background: #f8fafc; border: 2px solid var(--cl-line, #e2e8f0); border-radius: 12px;
  padding: 14px 12px; cursor: pointer; font-family: inherit; text-align: left;
  transition: border-color .15s, background .15s, transform .12s;
}
.bf-choice-card:hover:not(:disabled) { border-color: var(--cl-teal, #00A8B5); background: #f1fcfd; transform: translateY(-1px) }
.bf-choice-card:disabled { opacity: .5; cursor: not-allowed }
.bf-choice-icon { font-size: 24px; margin-bottom: 6px }
.bf-choice-h { font-size: 14px; font-weight: 900; color: var(--cl-navy, #1A3461); margin-bottom: 3px }
.bf-choice-p { font-size: 12px; color: var(--cl-muted, #64748b); line-height: 1.4 }

/* Form mini empresa */
.bf-form { display: flex; flex-direction: column; gap: 10px }
.bf-field { display: flex; flex-direction: column; gap: 4px }
.bf-field span { font-size: 12px; font-weight: 700; color: var(--cl-navy, #1A3461) }
.bf-field span em { color: #ef4444; font-style: normal; margin-left: 2px }
.bf-field input {
  padding: 9px 12px; border: 1.5px solid var(--cl-line, #e2e8f0); border-radius: 8px;
  font-size: 14px; font-family: inherit; color: var(--cl-navy, #1A3461);
  background: #fff; outline: none; transition: border-color .15s;
}
.bf-field input:focus { border-color: var(--cl-teal, #00A8B5) }
.bf-err {
  padding: 8px 10px; background: #fef2f2; border: 1px solid #fca5a5;
  border-radius: 8px; color: #7f1d1d; font-size: 12.5px;
}
.bf-form-actions { display: flex; gap: 8px; justify-content: space-between; margin-top: 4px }
.bf-btn-secondary {
  padding: 8px 14px; background: transparent; border: 1.5px solid var(--cl-line, #e2e8f0);
  border-radius: 8px; font-size: 13px; font-weight: 700; color: var(--cl-navy, #1A3461);
  cursor: pointer; font-family: inherit;
}
.bf-btn-primary {
  padding: 8px 16px; background: var(--cl-teal, #00A8B5); border: 0; color: #fff;
  border-radius: 8px; font-size: 13px; font-weight: 800; cursor: pointer; font-family: inherit;
}
.bf-btn-primary:disabled { opacity: .4; cursor: not-allowed }

/* Caso B: 1 perfil */
.bf-uno {
  display: flex; align-items: center; gap: 12px; padding: 10px 0 0;
  flex-wrap: wrap; justify-content: space-between;
}
.bf-uno-l { display: flex; gap: 10px; align-items: center; flex: 1; min-width: 0 }
.bf-tipo-pill {
  font-size: 11px; font-weight: 800; padding: 4px 10px; border-radius: 999px;
  text-transform: uppercase; letter-spacing: .04em; flex-shrink: 0;
}
.bf-tipo-pill.empresa { background: #eff6ff; color: #1d4ed8 }
.bf-tipo-pill.consumidor_final { background: #f0fdf4; color: #15803d }
.bf-uno-info { min-width: 0 }
.bf-uno-alias { font-size: 14px; font-weight: 800; color: var(--cl-navy, #1A3461) }
.bf-uno-rut { font-size: 12px; color: var(--cl-muted, #64748b); margin-top: 2px }
.bf-uno-link { font-size: 12px; font-weight: 800; color: var(--cl-teal, #00A8B5); text-decoration: none; white-space: nowrap }
.bf-uno-link:hover { text-decoration: underline }

/* Caso C: 2+ perfiles */
.bf-multi { display: flex; gap: 8px; align-items: center; margin-top: 6px }
.bf-select {
  flex: 1; padding: 10px 12px; border: 1.5px solid var(--cl-line, #e2e8f0);
  border-radius: 8px; font-size: 14px; font-family: inherit; color: var(--cl-navy, #1A3461);
  background: #fff; outline: none; cursor: pointer;
}
.bf-select:focus { border-color: var(--cl-teal, #00A8B5) }
.bf-multi-link {
  font-size: 12px; font-weight: 800; color: var(--cl-teal, #00A8B5);
  text-decoration: none; padding: 6px 10px; border: 1.5px solid currentColor;
  border-radius: 8px; white-space: nowrap;
}
.bf-multi-link:hover { background: var(--cl-teal, #00A8B5); color: #fff }
`
